import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import {
	useFlyerTemplates,
	useReadyToShare,
	useShareRecommendations,
} from 'src/app/pages/starterDashboardV2/shared/hooks';

import { useShareVisual } from '../../../GrowPetition/components/ShareVisual/hooks/useShareVisual';

import { buildSharingToolsData, type SharingToolsActions, type SharingToolsData } from './dataMapping';
import {
	createNavigationHandler,
	NAV_CONFIGS,
	type NavHandlerContext,
	type SharingToolsTrackingContext,
} from './navigation';

export type { SharingPostData } from './dataMapping';
export type BaseUseSharingToolsReturn = ModelHookResult<SharingToolsData, SharingToolsActions>;

export function useBaseSharingTools(
	trackingContext: SharingToolsTrackingContext = 'petition_dashboard_grow_petition_v2',
	options?: { imageVariantName?: string },
): BaseUseSharingToolsReturn {
	const {
		data: { petition },
	} = useStarterDashboardV2PageData();
	const recommendedGroups = useShareRecommendations(petition.slug);
	const readyToSharePosts = useReadyToShare();
	const shareVisual = useShareVisual();
	const flyerTemplates = useFlyerTemplates({
		petition,
		context: 'starter_dashboard_sharing_tools',
	});
	const navigate = useNavigate();
	const track = useTracking();

	const data = buildSharingToolsData({
		recommendedGroupsData: recommendedGroups.data,
		readyToSharePostsData: {
			isLoading: readyToSharePosts.data.isLoading,
			postTypeData: readyToSharePosts.data.postTypeData,
		},
		shareVisualData: shareVisual.data,
		flyerTemplates,
		petition,
		options,
	});

	const navContext: NavHandlerContext = {
		slug: petition.slug,
		navigate,
		track,
		trackingContext,
	};
	const navHandler = (config: (typeof NAV_CONFIGS)[keyof typeof NAV_CONFIGS]) =>
		createNavigationHandler(config, navContext);

	return {
		data,
		actions: {
			navigateToGroupRecommendations: navHandler(NAV_CONFIGS.groupRecommendations),
			navigateToAllTools: navHandler(NAV_CONFIGS.allTools),
			navigateToReadyToSharePosts: navHandler(NAV_CONFIGS.readyToSharePosts),
			navigateToEditPetition: navHandler(NAV_CONFIGS.editPetition),
			navigateToShareVisual: navHandler(NAV_CONFIGS.shareVisual),
			navigateToPosters: navHandler(NAV_CONFIGS.posters),
		},
	};
}
